import { Box, useColorModeValue, Grid, Skeleton, Text } from '@chakra-ui/react';
import { useContext } from 'react';
import { ClientDashboardStatsContext } from '../../../store/client/ClientDashboardStatsContext';
import KeyStatItem from './KeyStatItem';
import moment from 'moment';
import { CustomGreenUpArrowIcon } from '../../../theme/icons/CustomGreenUpArrowIcon';
import { CustomRedDownArrowIcon } from '../../../theme/icons/CustomRedDownArrowIcon';
import { CustomYellowUpArrowIcon } from '../../../theme/icons/CustomYellowUpArrowIcon';
import { MeContext } from '../../../context/MeContext';
import { useGetOnboardingStateQuery } from '../../../store/redux/api/onboardingApi';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

const KeyStats = ({ isLoading }) => {
  const modeBgDark = useColorModeValue('#243744', 'primaryDark');
  const dashboardCtx = useContext(ClientDashboardStatsContext);
  const { dashboardState } = dashboardCtx;
  const meCtx = useContext(MeContext);
  const { state, isSetUpConfirmed, isOrganisationActive } = meCtx;
  const displayPercentageChange = isSetUpConfirmed() && isOrganisationActive();

  const onboardingFlagOn = state.onboardingChecklistEnabled;
  const onboardingCompletedAtLogin = state.onboardingCompleted;
  const { data: onboardingData } = useGetOnboardingStateQuery(undefined, {
    skip: !onboardingFlagOn || onboardingCompletedAtLogin,
    refetchOnMountOrArgChange: true,
  });
  const onboardingCompleted =
    onboardingCompletedAtLogin || onboardingData?.completed === true;

  const isReadOnly = useIsReadOnly();
  const useDarkStyling = !onboardingFlagOn || onboardingCompleted || isReadOnly;
  const outerBg = useDarkStyling ? modeBgDark : 'white';
  const outerBoxShadow = useDarkStyling
    ? 'none'
    : '0px 5px 10px rgba(0, 0, 0, 0.05)';
  const titleColor = useDarkStyling ? 'white' : undefined;
  const tileBg = useDarkStyling ? undefined : 'softWhite';

  const formatPercentageChange = (percentageChange) => {
    return `${percentageChange >= 0 ? '+' : ''}${percentageChange}%`;
  };

  const determinePercentageChange = (text) => {
    const thisMonthNumber = dashboardState.keyStats[`${text}ThisMonth`];
    let lastMonthNumber = dashboardState.keyStats[`${text}LastMonth`];

    if (lastMonthNumber === 0 && thisMonthNumber === 0) {
      return 0;
    } else if (lastMonthNumber === 0 && thisMonthNumber === 1) {
      return 100;
    } else if (lastMonthNumber === 0) {
      lastMonthNumber = 1;
    }

    const percentageChange = lastMonthNumber
      ? ((thisMonthNumber - lastMonthNumber) / lastMonthNumber) * 100
      : 0;
    return percentageChange.toFixed(0);
  };

  const determineIcon = (text) => {
    const percentageChange = determinePercentageChange(text);
    if (percentageChange > 0) {
      return <CustomGreenUpArrowIcon boxSize="50px" />;
    } else if (percentageChange < 0) {
      return <CustomRedDownArrowIcon boxSize="50px" />;
    } else if (percentageChange === 0) {
      return <CustomYellowUpArrowIcon boxSize="50px" />;
    } else {
      return null;
    }
  };

  return (
    <Box
      bg={outerBg}
      borderRadius="12px"
      p={7}
      mb={8}
      pb={8}
      boxShadow={outerBoxShadow}
    >
      <Text color={titleColor} mb={6} textStyle="heading4">
        {moment().format('MMMM')} overview
      </Text>
      <Grid
        templateColumns={{
          base: 'repeat(1, 1fr)',
          md: 'repeat(2, 1fr)',
          xl: 'repeat(4, 1fr)',
        }}
        gap={8}
      >
        <Skeleton isLoaded={!isLoading} fadeDuration={1}>
          <KeyStatItem
            percentageChange={formatPercentageChange(
              determinePercentageChange('issued')
            )}
            icon={determineIcon('issued')}
            text="Issued passes"
            isLoading={isLoading}
            displayPercentageChange={displayPercentageChange}
            tileBg={tileBg}
          >
            {!isLoading && (
              <Text pt="10px">{dashboardState.keyStats.issuedThisMonth}</Text>
            )}
          </KeyStatItem>
        </Skeleton>
        <Skeleton isLoaded={!isLoading} fadeDuration={1}>
          <KeyStatItem
            percentageChange={formatPercentageChange(
              determinePercentageChange('addedToWallet')
            )}
            icon={determineIcon('addedToWallet')}
            text="Added to wallet"
            isLoading={isLoading}
            displayPercentageChange={displayPercentageChange}
            tileBg={tileBg}
          >
            {!isLoading && (
              <Text pt="10px">
                {dashboardState.keyStats.addedToWalletThisMonth}
              </Text>
            )}
          </KeyStatItem>
        </Skeleton>
        <Skeleton isLoaded={!isLoading} fadeDuration={1}>
          <KeyStatItem
            percentageChange={formatPercentageChange(
              determinePercentageChange('scans')
            )}
            icon={determineIcon('scans')}
            text="Scans"
            isLoading={isLoading}
            displayPercentageChange={displayPercentageChange}
            tileBg={tileBg}
          >
            {!isLoading && (
              <Text pt="10px">{dashboardState.keyStats.scansThisMonth}</Text>
            )}
          </KeyStatItem>
        </Skeleton>
        <Skeleton isLoaded={!isLoading} fadeDuration={1}>
          <KeyStatItem
            percentageChange={formatPercentageChange(
              determinePercentageChange('notifications')
            )}
            icon={determineIcon('notifications')}
            text="Notifications sent"
            isLoading={isLoading}
            displayPercentageChange={displayPercentageChange}
            tileBg={tileBg}
          >
            {!isLoading && (
              <Text pt="10px">
                {dashboardState.keyStats.notificationsThisMonth}
              </Text>
            )}
          </KeyStatItem>
        </Skeleton>
      </Grid>
    </Box>
  );
};

export default KeyStats;
